/**
 * TrackPoint domain model  
 * Represents a GPS tracking point with location and metadata
 */
import { formatDistance, formatSpeed } from '../../utils/unitUtils.js'

export class TrackPoint {
  constructor(data) {
    this.latitude = parseFloat(data.latitude) || 0
    this.longitude = parseFloat(data.longitude) || 0
    this.altitude = data.altitude !== undefined ? parseFloat(data.altitude) : null
    this.time = data.time ? new Date(data.time) : null
    this.speed = data.speed !== undefined ? parseFloat(data.speed) : null
    this.direction = data.direction !== undefined ? parseFloat(data.direction) : null
    this.distance = data.distance !== undefined ? parseFloat(data.distance) : null
    this.progress = data.progress !== undefined ? parseFloat(data.progress) : null
  }

  get hasAltitude() {
    return this.altitude !== null && !isNaN(this.altitude)
  }

  get hasSpeed() {
    return this.speed !== null && !isNaN(this.speed)
  }

  get hasDistance() {
    return this.distance !== null && !isNaN(this.distance) && this.distance > 0
  }

  get hasProgress() {
    return this.progress !== null && !isNaN(this.progress) && this.progress > 0
  }

  get isValidForDistanceCalculation() {
    return this.hasDistance && this.hasProgress
  }

  get isValid() {
    return !isNaN(this.latitude) && !isNaN(this.longitude) &&
           this.latitude !== 0 && this.longitude !== 0
  }

  /**
   * Get formatted speed with appropriate units
   */
  get speedKmh() {
    return this.hasSpeed ? formatSpeed(this.speed) : '-- km/h'
  }

  /**
   * Get formatted altitude in meters
   */
  get altitudeMeters() {
    return this.hasAltitude ? `${Math.round(this.altitude)} m` : '-- m'
  }

  /**
   * Get formatted time
   */
  get formattedTime() {
    return this.time ? this.time.toLocaleTimeString('pl-PL', {
      hour: '2-digit',
      minute: '2-digit'
    }) : '--:--'
  }

  /**
   * Get formatted distance with appropriate units
   */
  get distanceKm() {
    return this.hasDistance ? formatDistance(this.distance) : '-- km'
  }

  /**
   * Get progress as percentage
   */
  get progressPercent() {
    return this.hasProgress ? `${this.progress.toFixed(1)}%` : '--'
  }

  static fromApiData(apiTrack) {
    return new TrackPoint({
      latitude: apiTrack.latitude,
      longitude: apiTrack.longitude,
      altitude: apiTrack.altitude,
      time: apiTrack.time,
      speed: apiTrack.speed,
      direction: apiTrack.direction,
      distance: apiTrack.distance,
      progress: apiTrack.progress
    })
  }
}